import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { useTranslation } from "react-i18next";
import { AUDIT_TOKENS } from "../../theme/auditTokens";

interface Props {
  /** Vehicles whose trips have arrived; the Geotab client batches the calls, so this often jumps from 0 to `total`. */
  loaded: number;
  total: number;
  loadingTrips: boolean;
  loadingDetails: boolean;
}

/**
 * Short bar with the phase named next to it, inline in the table footer while the period loads.
 * Determinate only while vehicles actually come in one by one; otherwise it moves on its own:
 * trips first, then the figures behind the rows.
 */
export function LoadProgress({ loaded, total, loadingTrips, loadingDetails }: Props) {
  const { t } = useTranslation();
  if (!loadingTrips && !loadingDetails) return null;
  const partial = loadingTrips && loaded > 0 && loaded < total;
  const label = loadingTrips
    ? partial
      ? t("logbookAudit.progress.vehicles", { loaded, total })
      : t("logbookAudit.progress.trips", { count: total })
    : t("logbookAudit.progress.details");
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }} role="status" aria-live="polite">
      <LinearProgress
        variant={partial ? "determinate" : "indeterminate"}
        value={partial ? Math.round((loaded / total) * 100) : undefined}
        aria-label={label}
        sx={{ width: 96, height: 3, borderRadius: 2, bgcolor: AUDIT_TOKENS.line, "& .MuiLinearProgress-bar": { bgcolor: AUDIT_TOKENS.accent } }}
      />
      <Box component="span" sx={{ fontSize: 12, color: AUDIT_TOKENS.muted, fontVariantNumeric: "tabular-nums" }}>
        {label}
      </Box>
    </Box>
  );
}
